/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef } from 'react';

import styles from '../../styles/fields/text-field.module.css';

interface TextareaFieldProps {
    name?: string | null;
    id?: string | null;
    value?: string | null;
    errors?: string[] | null;
    placeholder?: string | null;
    autoComplete?: string | null;
    withoutAutocomplete?: boolean;
    withBorder?: boolean;
    required?: boolean;
    disabled?: boolean;
    maxLength?: number;
    className?: string | null;
    onChange?: ((value: string | null, e: React.ChangeEvent<HTMLTextAreaElement>) => void) | null;
    onInput?: ((e: React.FormEvent<HTMLTextAreaElement>) => void) | null;
    onFocus?: React.FocusEventHandler<HTMLTextAreaElement> | null;
    onBlur?: React.FocusEventHandler<HTMLTextAreaElement> | null;
    onPointerUp?: React.PointerEventHandler<HTMLTextAreaElement> | null;
    inputRef?: React.Ref<HTMLTextAreaElement> | null;
}

function TextareaField({
    name = null,
    id = null,
    value = null,
    errors = null,
    placeholder = null,
    autoComplete = null,
    withoutAutocomplete = false,
    withBorder = false,
    required = false,
    disabled = false,
    className = null,
    maxLength = null,
    onChange = null,
    onInput: onCustomInput = null,
    onFocus = null,
    onBlur = null,
    onPointerUp = null,
    inputRef = null,
}: TextareaFieldProps) {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    const onInputChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            if (errors !== null) {
                internalRef.current?.setCustomValidity('');
                // internalRef.current.reportValidity();
            }
            const newValue = e.currentTarget.value !== '' ? e.currentTarget.value : null;
            if (onChange !== null) {
                onChange(newValue, e);
            }
        },
        [onChange, errors],
    );

    const onInput = useCallback(
        (e: React.FormEvent<HTMLTextAreaElement>) => {
            if (errors !== null) {
                internalRef.current?.setCustomValidity('');
            }
            if (onCustomInput !== null) {
                onCustomInput(e);
            }
        },
        [onCustomInput, errors],
    );

    useEffect(() => {
        if (internalRef.current) {
            internalRef.current.setCustomValidity(
                errors !== null && errors.length > 0 ? errors[0] : '',
            );
            if (errors !== null && errors.length > 0) {
                internalRef.current.reportValidity();
            }
        }
    }, [errors]);

    return (
        <textarea
            ref={(ref) => {
                internalRef.current = ref;
                if (inputRef !== null) {
                    if (typeof inputRef === 'function') inputRef(ref);
                    else if (inputRef) {
                        (inputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current =
                            ref;
                    }
                }
            }}
            name={name || undefined}
            id={id || undefined}
            value={value || ''}
            placeholder={placeholder || undefined}
            required={required}
            disabled={disabled}
            className={classNames([
                styles.container,
                {
                    [styles.withBorder]: withBorder,
                    [className as string]: className !== null,
                },
            ])}
            maxLength={maxLength || undefined}
            onChange={onInputChange}
            onInput={onInput}
            onFocus={onFocus || undefined}
            onBlur={onBlur || undefined}
            onPointerUp={onPointerUp || undefined}
            {...(autoComplete !== null ? { autoComplete } : {})}
            {...(withoutAutocomplete ? { autoComplete: 'off' } : {})}
        />
    );
}

const ForwardedTextareaField = React.forwardRef<
    HTMLTextAreaElement,
    Omit<TextareaFieldProps, 'inputRef'>
>((props, ref) => <TextareaField {...props} inputRef={ref} />);
ForwardedTextareaField.displayName = 'TextareaField';

export default ForwardedTextareaField;
